import { CalendarClock, Menu, Mic, Plus, Sparkles, SquareTerminal, Target } from "lucide-react";
import { GlobalSearchButton } from "../GlobalSearch";
import { Button } from "../ui/Button";

interface Props {
  operationId: string;
  title: string;
  missionCount: number;
  onCreateTask: () => void;
  onVoiceTask: () => void;
  onShowTasks: () => void;
  onMenuClick?: () => void;
}

export function OperationHeader({ operationId, title, missionCount, onCreateTask, onVoiceTask, onShowTasks, onMenuClick }: Props) {
  const now = new Date();
  const dateLabel = new Intl.DateTimeFormat("ja-JP", {
    month: "numeric",
    day: "numeric",
    weekday: "short",
  }).format(now);

  return (
    <header className="command-header px-5 py-5 md:px-8 md:py-7">
      <div className="flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between">
        <div className="min-w-0">
          <div className="command-dim flex flex-wrap items-center gap-2 text-xs">
            {onMenuClick && (
              <button
                type="button"
                onClick={onMenuClick}
                aria-label="メニューを開く"
                className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-border bg-bg text-muted md:hidden dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-200"
              >
                <Menu className="h-4 w-4" />
              </button>
            )}
            <span className="inline-flex items-center gap-1 rounded-full border border-brand-200 bg-brand-50 px-2.5 py-1 font-mono text-brand-700 dark:border-indigo-400/30 dark:bg-indigo-400/10 dark:text-indigo-100">
              <SquareTerminal className="h-3.5 w-3.5" />
              {operationId}
            </span>
            <span className="inline-flex items-center gap-1 rounded-full border border-emerald-200 bg-emerald-50 px-2.5 py-1 text-emerald-700 dark:border-emerald-400/25 dark:bg-emerald-400/10 dark:text-emerald-100">
              <CalendarClock className="h-3.5 w-3.5" />
              {dateLabel}
            </span>
            <span className="inline-flex items-center gap-1 rounded-full border border-amber-200 bg-amber-50 px-2.5 py-1 text-amber-700 dark:border-amber-400/25 dark:bg-amber-400/10 dark:text-amber-100">
              <Target className="h-3.5 w-3.5" />
              ミッション {missionCount}件
            </span>
          </div>
          <div className="mt-4 flex items-start gap-3">
            <div className="mt-1 flex h-10 w-10 shrink-0 items-center justify-center rounded-md border border-brand-200 bg-brand-50 text-brand-700 dark:border-indigo-300/30 dark:bg-indigo-400/15 dark:text-indigo-100">
              <Sparkles className="h-5 w-5" />
            </div>
            <div className="min-w-0">
              <p className="command-dim text-sm font-medium">今日の作戦</p>
              <h1 className="mt-1 break-words text-3xl font-bold text-text dark:text-white md:text-5xl">
                {title}
              </h1>
            </div>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <GlobalSearchButton className="border-border bg-card text-muted hover:bg-hover dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-200 dark:hover:bg-zinc-800" />
          <Button variant="outline" onClick={onShowTasks}>
            タスク一覧
          </Button>
          <Button
            variant="outline"
            leftIcon={<Mic className="h-4 w-4" />}
            onClick={onVoiceTask}
            className="border-brand-200 bg-brand-50 text-brand-700 hover:bg-brand-100 dark:border-indigo-400/40 dark:bg-indigo-400/10 dark:text-indigo-100 dark:hover:bg-indigo-400/20"
          >
            音声AI追加
          </Button>
          <Button variant="primary" leftIcon={<Plus className="h-4 w-4" />} onClick={onCreateTask}>
            ミッション登録
          </Button>
        </div>
      </div>
    </header>
  );
}
